import React from 'react';
import { NextPage } from 'next';
import { PAGINATED_GUIDES_QUERY } from '../client/graphql/queries/guide';
import { buildPageableSEO } from '../client/utils/seo/seo';
import { QueryObject } from '../client/utils/links';
import { Adv } from '../domain/adv';
import { buildArticlesURLObject } from '../client/utils/links/articles';
import { ArticleType } from '../domain/articleType';
import GuidesPage, { GuidesPageProps, PAGE_SIZE } from '../client/templates/common/guides-page/GuidesPage';
import { getAdvByType } from '../services/advs/advs';
import { RelatedArticleItem } from '../client/components/related-articles/RelatedArticles';
import { AdvFluidType, Target } from '../domain/graphcms';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { importDynamicData } from '../services/importDynamicData';
import httpErrors from '../client/utils/error/http/httpErrors';
import { ASSET_BY_IDS } from '../client/graphql/queries/asset';

export interface EducationSerpProps {
  query: QueryObject,
  SEO: {
    title: string,
    description: string,
  },
  advSerp: Adv,
  advSide: Adv,
  page: number,
  totalPages: number,
  totalItems: number,
  guides: any[],
  bottomBar: GuidesPageProps['bottomBar'],
  promotionalLinks: Omit<RelatedArticleItem, 'onClick'>[],
}

export const EducationSerp: NextPage<EducationSerpProps> = ({
  query,
  SEO,
  advSerp,
  advSide,
  page,
  totalPages,
  totalItems,
  guides,
  bottomBar,
  promotionalLinks,
}) => (
  <GuidesPage
    query={query}
    title="Contenuti e Corsi per diventare un agente immobiliare"
    advSerp={advSerp}
    advSide={advSide}
    articleType={ArticleType.EDUCATION}
    SEO={SEO}
    guides={guides}
    page={page}
    totalPages={totalPages}
    totalItems={totalItems}
    promotionalLinks={promotionalLinks}
    bottomBar={bottomBar}
  />
);

export const getServerSideProps = withGetServerSideError<EducationSerpProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();
  const articlesURLObject = buildArticlesURLObject(query, ArticleType.EDUCATION);

  const page = query.p ? Number.parseInt(query.p as string, 10) : 1;
  if (Number.isNaN(page) || page < 1) {
    throw new httpErrors.BadRequest(`Invalid page parameter ${page}`);
  }

  const graphqlQuery = {
    query: PAGINATED_GUIDES_QUERY,
    variables: {
      skip: PAGE_SIZE * (page - 1),
      first: PAGE_SIZE,
      target: [Target.AGENTE],
    },
  };

  const [
    { SEO: seoTemplate, bottomBar, promotionalLinks },
    adv,
    { data },
  ] = await Promise.all([
    importDynamicData('pages/education.yml'),
    getAdvByType(AdvFluidType.AGENT_JOB_APPLICATION),
    graphQLClient.query(graphqlQuery),
  ]);

  const enrichedPromotionalLinks = await graphQLClient.query({
    query: ASSET_BY_IDS,
    variables: {
      ids: promotionalLinks.map((link) => link.thumbnail.handle),
    },
  });

  const promotionalLinksAssetUrls = enrichedPromotionalLinks.data.assets.reduce((acc, asset) => {
    acc[asset.handle] = asset.url;
    return acc;
  }, {});

  const totalItems = data.total.aggregate.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  if (page > totalPages && totalPages > 0) {
    throw new httpErrors.NotFound(`Page parameter ${page} greater than total pages ${totalPages}`);
  }

  const pageable = {
    page,
    first: page === 1,
    last: page === totalPages,
    hasResults: data.guides.length > 0,
  };

  return {
    props: {
      query,
      advSerp: adv,
      advSide: adv,
      SEO: buildPageableSEO({
        ...seoTemplate,
        urlObject: articlesURLObject,
      }, pageable),
      guides: data.guides,
      page,
      totalPages,
      totalItems,
      bottomBar,
      promotionalLinks: promotionalLinks?.map((link) => ({
        ...link,
        thumbnail: {
          ...link.thumbnail,
          url: promotionalLinksAssetUrls[link.thumbnail.handle],
        },
      })) ?? [],
    },
  };
});

export default withError(EducationSerp);
